/**
 * OutlineGenerationNotifier
 *
 * Mounted once in AppLayout. Watches the outline-generation store and shows
 * router-aware toasts when generation completes or fails. The store itself
 * lives outside React (so the SSE survives navigation); this component is the
 * thin React bridge that turns store state transitions into actionable UI.
 *
 * Why a component (not store-fired toasts): the "View outline" action needs
 * `useNavigate` so the click stays inside React Router instead of doing a full
 * page reload.
 *
 * @author Tutara Team
 * @since 2026-05 (non-blocking outline generation)
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutlineGenerationStore } from '@/stores/outline-generation-store';
import { toast } from '@/utils/toast';

export function OutlineGenerationNotifier() {
  const navigate = useNavigate();
  const status = useOutlineGenerationStore((s) => s.status);
  const errorMessage = useOutlineGenerationStore((s) => s.errorMessage);
  const errorCode = useOutlineGenerationStore((s) => s.errorCode);
  const persistedTick = useOutlineGenerationStore((s) => s.persistedTick);
  const lastPersistedOutlineId = useOutlineGenerationStore((s) => s.lastPersistedOutlineId);
  const lastPersistedTopic = useOutlineGenerationStore((s) => s.lastPersistedTopic);

  // Track the last status/persistedTick we toasted on so we only fire once
  // per transition (subsequent re-renders with the same state are no-ops).
  const lastNotifiedTick = useRef(0);
  const lastNotifiedFailure = useRef<string | null>(null);

  // Success toast — fires once per persisted outline.
  useEffect(() => {
    if (status !== 'completed' || persistedTick === lastNotifiedTick.current) return;
    if (!lastPersistedOutlineId) return;
    lastNotifiedTick.current = persistedTick;
    toast.action({
      message: `Outline "${lastPersistedTopic ?? 'Untitled'}" is ready.`,
      actionLabel: 'View',
      type: 'success',
      onAction: () => {
        navigate(`/admin/tutor/outlines/${lastPersistedOutlineId}`);
        useOutlineGenerationStore.getState().dismiss();
      },
    });
  }, [status, persistedTick, lastPersistedOutlineId, lastPersistedTopic, navigate]);

  // Failure toast — fires once per failure transition. We key the dedup on the
  // error message so a retry → fail produces a fresh toast.
  useEffect(() => {
    if (status !== 'failed' || !errorMessage) return;
    if (lastNotifiedFailure.current === errorMessage) return;
    lastNotifiedFailure.current = errorMessage;

    // AGENT failures aren't usefully retryable; skip the Retry affordance.
    if (errorCode === 'AGENT') {
      toast.error(`Outline generation failed: ${errorMessage}`);
      return;
    }
    toast.action({
      message: `Outline generation failed: ${errorMessage}`,
      actionLabel: 'Retry',
      type: 'error',
      onAction: () => {
        useOutlineGenerationStore.getState().retry();
      },
    });
  }, [status, errorMessage, errorCode]);

  // Clear failure dedup when we leave the failed state, so a future failure
  // with the same message can re-notify.
  useEffect(() => {
    if (status !== 'failed') {
      lastNotifiedFailure.current = null;
    }
  }, [status]);

  return null;
}

export default OutlineGenerationNotifier;
